import React, { useState, useEffect } from "react";
import "./FinancialPerformance.css";
import { useAuthFetch } from "../hooks/useAuthFetch";

function FinancialPerformance({ projectData = {} }) {
  const { authFetch, isReady } = useAuthFetch();
  const [performanceData, setPerformanceData] = useState({
    billingProgress: 0,
    costPerformance: 0,
    actualRevenue: 0,
    revisedRevenue: 0,
    actualExpense: 0,
    revisedExpense: 0
  });

  useEffect(() => {
    const fetchFinancialData = async () => {
      if (!projectData?.projectId || !projectData?.division || !isReady) return;

      try {
        const response = await authFetch(
          `${process.env.REACT_APP_BACKEND_BASEURL}/api/finicial-budget?projectID=${projectData.projectId}&divisionName=${projectData.division}`
        );
        const data = await response.json();

        // Find the values from budgetProgressData array
        const findValue = (label) => {
          const item = data.budgetProgressData.find(item => item.label === label);
          return item ? parseFloat(item.value) : 0;
        };

        setPerformanceData({
          billingProgress: findValue("Billing Progress"),
          costPerformance: findValue("Cost Performance"),
          actualRevenue: findValue("Actual Revenue Amount"),
          revisedRevenue: findValue("Revised Revenue Budget"),
          actualExpense: findValue("Actual Expense Amount"),
          revisedExpense: findValue("Revised Budget Expense")
        });
      } catch (error) {
        console.error('Error fetching financial data:', error);
      }
    };

    fetchFinancialData();
  }, [projectData?.projectId, projectData?.division, authFetch, isReady]);

  // Helper function to format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <section className="financial-performance">
      <div className="performance-header">
        <h2 className="performance-title">Financial Performance</h2>
        <div className="legend">
          <div className="legend-item">
            <div className="legend-color billed"></div>
            <div className="legend-label">Billed to Date</div>
          </div>
          <div className="legend-item">
            <div className="legend-color total"></div>
            <div className="legend-label">Total Contract</div>
          </div>
        </div>
      </div>
      <div className="performance-metrics">
        <div className="metric">
          <div className="flex justify-between items-center mb-2 max-md:mb-1.5">
            <h3 className="metric-title">Billing Progress</h3>
            <span className="text-black-700 text-sm max-md:text-xs font-medium">
              {performanceData.billingProgress}%
            </span>
          </div>
          <div className="progress-container relative">
            <div className="progress-bar">
              <div
                className="progress-fill relative"
                style={{ 
                  width: `${Math.min(performanceData.billingProgress, 100)}%`,
                  backgroundColor: performanceData.billingProgress > 100 ? '#ef4444' : '#10b981'
                }}
                aria-label={`Billing progress: ${performanceData.billingProgress}%`}
              />
            </div>
          </div>
          <div className="progress-values">
            <div className="progress-start">{formatCurrency(performanceData.actualRevenue)}</div>
            <div className="progress-end">{formatCurrency(performanceData.revisedRevenue)}</div>
          </div>
        </div>
        <div className="metric">
          <div className="flex justify-between items-center mb-2 max-md:mb-1.5">
            <h3 className="metric-title">Cost Performance</h3>
            <span className="text-black-700 text-sm max-md:text-xs font-medium">
              {performanceData.costPerformance}%
            </span>
          </div>
          <div className="progress-container relative">
            <div className="progress-bar">
              <div
                className="progress-fill relative"
                style={{ 
                  width: `${Math.min(performanceData.costPerformance, 100)}%`,
                  backgroundColor: performanceData.costPerformance > 100 ? '#ef4444' : '#10b981'
                }}
                aria-label={`Cost performance: ${performanceData.costPerformance}%`}
              />
            </div>
          </div>
          <div className="progress-values">
            <div className="progress-start">{formatCurrency(performanceData.actualExpense)}</div>
            <div className="progress-end">{formatCurrency(performanceData.revisedExpense)}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default FinancialPerformance;
